
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useState } from "react";
import { TablePagination } from "../ui/data-table/TablePagination";
import { useTransactions } from "./hooks/useTransactions";
import { TransactionGroup } from "./TransactionGroup";
import { Card } from "../ui/card";

interface TransactionTableProps {
  transactionType?: string;
  period?: string;
  selectedDate?: Date;
  validationStatus?: string;
}

export const TransactionTable = ({ 
  transactionType, 
  period = "all", 
  selectedDate,
  validationStatus 
}: TransactionTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const { data: transactions, isLoading } = useTransactions(
    transactionType, 
    period, 
    selectedDate
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-gray-500 text-center">Aucune transaction trouvée</p>
      </Card>
    );
  }

  // Filtrer les transactions selon le statut de validation
  const filteredTransactions = validationStatus === undefined || validationStatus === "all" 
    ? transactions
    : validationStatus === "validated"
      ? transactions.filter(t => t.validation_status === "validated" || t.is_deducted_from_revenue)
      : transactions.filter(t => t.validation_status === "pending" && !t.is_deducted_from_revenue);

  // Grouper les transactions par date de manière plus efficace
  const groupedTransactions = filteredTransactions.reduce((groups: any, transaction) => {
    const date = format(new Date(transaction.created_at), 'PP', { locale: fr });
    if (!groups[date]) {
      groups[date] = {
        date,
        transactions: [],
        total: 0
      };
    }
    groups[date].transactions.push(transaction);
    
    // Calculer le total au fur et à mesure
    if (transaction.transaction_type === 'sale') {
      groups[date].total += transaction.total_amount || 0;
    } else if (transaction.transaction_type === 'credit_payment') {
      groups[date].total += transaction.paid_amount || 0;
    } else if (transaction.transaction_type === 'return') {
      groups[date].total -= transaction.refund_amount || 0;
    }
    
    return groups;
  }, {});

  // Convertir en tableau et trier
  const dates = Object.values(groupedTransactions)
    .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const totalPages = Math.ceil(dates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDates = dates.slice(startIndex, endIndex);

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {currentDates.map((group: any) => (
          <TransactionGroup
            key={group.date}
            date={group.date}
            transactions={group.transactions}
            total={group.total}
            transactionType={transactionType}
          />
        ))}
      </div>
      {totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};
